import { useEffect, useRef } from "react";

interface SEOHeadInput {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>[];
}

const FALLBACK_DOMAIN = "https://mulebuyspreadsheets.hair";

function getSiteUrl(): string {
  if (typeof import.meta !== "undefined" && (import.meta as Record<string, unknown>).env) {
    const env = (import.meta as Record<string, { VITE_SITE_URL?: string }>).env;
    if (env.VITE_SITE_URL) return env.VITE_SITE_URL.replace(/\/+$/, "");
  }
  return FALLBACK_DOMAIN;
}

function setMetaAttribute(attrName: string, content: string, isProperty: boolean) {
  const attr = isProperty ? "property" : "name";
  const selector = `meta[${attr}="${attrName}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrName);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export function useSEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  ogType = "website",
  jsonLd,
}: SEOHeadInput) {
  const injectedScriptIds = useRef<string[]>([]);
  const siteUrl = getSiteUrl();
  const canonical = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;

  useEffect(() => {
    document.title = title;

    setMetaAttribute("description", description, false);
    setMetaAttribute("robots", "index, follow, max-image-preview:large", false);
    if (keywords) setMetaAttribute("keywords", keywords, false);

    setLinkCanonical(canonical);

    setMetaAttribute("og:title", title, true);
    setMetaAttribute("og:description", description, true);
    setMetaAttribute("og:type", ogType, true);
    setMetaAttribute("og:url", canonical, true);
    setMetaAttribute("og:site_name", "MulebuySpreadsheets", true);
    if (ogImage) setMetaAttribute("og:image", ogImage, true);

    setMetaAttribute("twitter:card", "summary_large_image", false);
    setMetaAttribute("twitter:title", title, false);
    setMetaAttribute("twitter:description", description, false);
    if (ogImage) setMetaAttribute("twitter:image", ogImage, false);

    injectedScriptIds.current.forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.remove();
    });
    injectedScriptIds.current = [];

    if (jsonLd && jsonLd.length > 0) {
      jsonLd.forEach((ld, i) => {
        const id = `seo-jsonld-${i}`;
        injectedScriptIds.current.push(id);
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = id;
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);
      });
    }
  }, [title, description, keywords, canonical, ogImage, ogType, jsonLd]);

  useEffect(() => {
    return () => {
      injectedScriptIds.current.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);
}